import { useState, useEffect } from 'react';
import { LineChart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';

const MARKET_MODELS = [
  { key: 'feed_in_tariff', labelKey: 'am_model_feed_in' },
  { key: 'ppa', labelKey: 'am_model_ppa' },
  { key: 'market_trading', labelKey: 'am_model_trading' },
  { key: 'self_consumption', labelKey: 'am_model_self' },
  { key: 'hybrid', labelKey: 'am_model_hybrid' },
];

interface MarketConfig {
  marketModel?: string;
  balancingGroup?: string;
  imbalanceResponsibleParty?: string;
  tradingZone?: string;
  settlementModel?: string;
}

interface Props {
  assetId: string;
  initialData?: MarketConfig;
  onSaved: () => void;
}

export default function TabMarket({ assetId, initialData, onSaved }: Props) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [form, setForm] = useState({
    marketModel: '',
    balancingGroup: '',
    imbalanceResponsibleParty: '',
    tradingZone: '',
    settlementModel: '',
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        marketModel: initialData.marketModel ?? '',
        balancingGroup: initialData.balancingGroup ?? '',
        imbalanceResponsibleParty: initialData.imbalanceResponsibleParty ?? '',
        tradingZone: initialData.tradingZone ?? '',
        settlementModel: initialData.settlementModel ?? '',
      });
    }
  }, [initialData]);

  const handleSave = async () => {
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {};
      if (form.marketModel) payload.marketModel = form.marketModel;
      if (form.balancingGroup) payload.balancingGroup = form.balancingGroup;
      if (form.imbalanceResponsibleParty) payload.imbalanceResponsibleParty = form.imbalanceResponsibleParty;
      if (form.tradingZone) payload.tradingZone = form.tradingZone;
      if (form.settlementModel) payload.settlementModel = form.settlementModel;

      await api.patch(`/assets/${assetId}/market-config`, payload);
      addToast(t('am_saved_ok'), 'success');
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, t('am_save_failed'));
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const inputBg = 'var(--surface-solid)';
  const inputBorder = 'var(--line-strong)';
  const sectionBg = 'var(--surface-2)';

  const inputClass = "w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-1 focus:ring-[color:var(--accent)] transition-all whitespace-nowrap";
  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: textMain,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
            <LineChart size={15} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>{t('am_title')}</h3>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Trading model, balancing group and settlement settings</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
          style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
        >
          {saving ? (
            <><i className="ri-loader-4-line animate-spin"></i> Saving...</>
          ) : (
            <><i className="ri-save-line"></i> Save</>
          )}
        </button>
      </div>

      {apiError && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
          <i className="ri-error-warning-line mr-2"></i>{apiError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>{t('am_model')}</label>
          <select
            value={form.marketModel}
            onChange={(e) => setForm((prev) => ({ ...prev, marketModel: e.target.value }))}
            className={inputClass}
            style={inputStyle}
          >
            <option value="">{t('am_model_ph')}</option>
            {MARKET_MODELS.map((m) => (
              <option key={m.key} value={m.key}>{t(m.labelKey)}</option>
            ))}
          </select>
          {form.marketModel && (
            <div className="mt-2 px-3 py-2 rounded-md text-xs" style={{ background: sectionBg, color: textMuted }}>
              {form.marketModel === 'feed_in_tariff' && t('am_desc_feed_in')}
              {form.marketModel === 'ppa' && t('am_desc_ppa')}
              {form.marketModel === 'market_trading' && t('am_desc_trading')}
              {form.marketModel === 'self_consumption' && t('am_desc_self')}
              {form.marketModel === 'hybrid' && t('am_desc_hybrid')}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>{t('am_balancing')}</label>
          <input
            type="text"
            value={form.balancingGroup}
            onChange={(e) => setForm((prev) => ({ ...prev, balancingGroup: e.target.value }))}
            placeholder={`${t('common_eg')} BG-UA-HELIOS-01`}
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>{t('am_irp')}</label>
          <input
            type="text"
            value={form.imbalanceResponsibleParty}
            onChange={(e) => setForm((prev) => ({ ...prev, imbalanceResponsibleParty: e.target.value }))}
            placeholder={`${t('common_eg')} Helios Aggregate LLC`}
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>{t('am_zone')}</label>
          <input
            type="text"
            value={form.tradingZone}
            onChange={(e) => setForm((prev) => ({ ...prev, tradingZone: e.target.value }))}
            placeholder={`${t('common_eg')} UA-IPS, UA-BEI`}
            className={inputClass}
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>{t('am_settlement')}</label>
          <input
            type="text"
            value={form.settlementModel}
            onChange={(e) => setForm((prev) => ({ ...prev, settlementModel: e.target.value }))}
            placeholder={`${t('common_eg')} Monthly, Hourly`}
            className={inputClass}
            style={inputStyle}
          />
        </div>
      </div>
    </div>
  );
}
